import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { map, tap } from 'rxjs/operators';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  currUser;
  private config: any; // new property to store config

  constructor(
    private http: HttpClient,
    private router: Router,
    private configService: ConfigService
  ) {
    this.currUser = this.getCurrentUser();
  }

  login(email: string, password: string) {
    return this.http.post<any>(`${environment.apiUrl}/users/authorize`, { email, password })
      .pipe(map(user => {
        if (user && user.token) {
          localStorage.setItem('currentUser', JSON.stringify(user));
        }
        this.currUser = user;

        return user;
      }));
  }

  loginAs(userId) {
    return this.http.post<any>(`${environment.apiUrl}/users/login_as`, { userId })
      .pipe(map(user => {
        if (user && user.token) {
          localStorage.setItem('currentUser', JSON.stringify(user));
        }
        this.currUser = user;

        return user;
      }));
  }

  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');
    this.currUser = null;
    this.router.navigate(['/session/login']);
  }

  getCurrentUser() {
    return JSON.parse(localStorage.getItem('currentUser') || 'null');
  }

  getMe() {
    return this.http.get<any>(`${environment.apiUrl}/users/me/${this.currUser._id}`);
  }

  getCurrentOffice() {
    if (!this.currUser) {
      return null;
    }

    return this.currUser.offices && this.currUser.offices.length > 0 ? this.currUser.offices[0] : null;
  }

  userIsAccounting() {
    if (!this.currUser) {
      return false;
    }

    return this.currUser.roles.includes('accounting') || this.userIsAdmin();
  }

  userIsOfficeManager() {
    if (!this.currUser) {
      return false;
    }

    return this.currUser.roles.includes('office_manager') || this.userIsAdmin();
  }

  userIsEstimator() {
    if (!this.currUser) {
      return false;
    }

    return this.currUser.roles.includes('estimator');
  }

  userIsAdmin() {
    if (!this.currUser) {
      return false;
    }

    return this.currUser.isAdmin || this.currUser.roles.includes('admin');
  }

  userIsCorporate() {
    if (!this.currUser) {
      return false;
    }

    return this.currUser.roles.includes('corporate');
  }

  userIsInstallerOnly() {
    if (!this.currUser) {
      return false;
    }

    if (this.userIsAdmin() ||
      this.userIsAccounting() ||
      this.userIsEstimator() ||
      this.userIsOfficeManager() ||
      this.userIsCorporate()) {
      return false;
    }

    return this.currUser.roles.includes('installer');
  }

  userIsEstimatorOnly() {
    if (!this.currUser) {
      return false;
    }

    if (this.userIsAdmin() ||
      this.userIsAdmin() ||
      this.userIsAccounting() ||
      this.userIsInstallerOnly() ||
      this.userIsOfficeManager() ||
      this.userIsCorporate()) {
      return false;
    }

    return this.currUser.roles.includes('estimator');
  }

  userIs(roles: string[]) {
    if (!this.currUser) {
      return false;
    }

    return roles.some(role => this.currUser.roles.includes(role));
  }

  // New method to load config from backend on initializeApp in app.module.ts
  loadConfig() {
    return this.configService.getConfig()
      .pipe(tap(cfg => this.config = { googleClientId: cfg.oauth.clientID }));
  }

  // New method to return cached/final config
  getConfig() {
    return this.config;
  }

  googleCallback(params) {
    return this.http.get<any>(`${environment.apiUrl}/google`, { responseType: 'json', params })
      .pipe(map(user => {
        console.log('googleCallback', user);
        if (user && user.token) {
          localStorage.setItem('currentUser', JSON.stringify(user));
        }
        this.currUser = user;

        return user;
      }));;
  }

  updateDbConfig(data) {
    return this.http.put<any>(`${environment.apiUrl}/google/config`, data);
  }

  getDbConfig() {
    return this.http.get<any>(`${environment.apiUrl}/google/config`);
  }

  // Amazon S3 config endpoints
  getS3Config() {
    return this.http.get<any>(`${environment.apiUrl}/amazon-s3/config`);
  }

  updateS3Config(data) {
    return this.http.put<any>(`${environment.apiUrl}/amazon-s3/config`, data);
  }

}
